import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { Button, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TextFieldWrapper from '../../micro/TextFieldWrapper/TextFieldWrapper';
import { loginAPIcall, selfRegisterAPIcall, setLoginError } from '../../../redux/actions/auth/auth.actions';
import HomeNavBar from '../../micro/HomeNavBar/HomeNavBar';
import HomeFooter from '../../micro/HomeFooter/HomeFooter';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import { lightGrey } from '../../../app/theme';
import AppState from '../../../models/redux/state/app.state';
import history from '../../../services/browserHistory';

const useStyles = makeStyles((theme: Theme) => ({
  textLinkButtons: {
    borderRadius: 0,
    height: '1em',
    padding: 0,
    marginLeft: '0.25em',
    textTransform: 'none',
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
    fontSize: 16,
    textDecoration: 'underline',
    verticalAlign: 'baseline',
  },
  images: {
    display: 'block',
    width: '100%',
  },
  bedLogo: {
    paddingLeft: 65,
    paddingTop: 16,
    width: 239,
    height: 63,
  },
  doctorImage: {
    backgroundImage: `url(${BASE_URL}${IMAGE_PATHS.PHYSICIANS_CHATTING.path})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    borderRadius: '0 16px 16px 0',
    width: '45%',
    height: 578,
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    height: '100vh',
  },
  signInContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '3em 0',
  },
  signInForm: {
    width: '45%',
    marginRight: '4em',
  },
  signInTitle: {
    marginBottom: '1.5em',
    paddingBottom: '0.5em',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    color: theme.palette.secondary.main,
    fontSize: '2em',
    fontWeight: 600,
  },
  signInButton: {
    textTransform: 'uppercase',
    fontWeight: 600,
    borderRadius: 1000,
    padding: '0.5em 2em',
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 475,
  },
  fieldsInfo: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  requestAccess: {
    margin: '1em 0',
    display: 'flex',
    alignItems: 'baseline',
    fontSize: 16,
  },
  inputField: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  forgotPasswordButton: {
    textTransform: 'none',
    textDecoration: 'underline',
    fontStyle: 'italic',
    fontSize: 16,
  },
  error: {
    marginTop: 10,
    color: 'red',
    fontSize: 16,
    lineHeight: 1.2,
    width: 'calc(100% + 100px)',
  },
  formColumn: {
    width: '90%',
    display: 'flex',
    flexDirection: 'column',
  },
  requiredField: {
    margin: '0 0 1em 0',
    textAlign: 'right',
  },
  passwordInfo: {
    width: '30%',
    fontSize: 16,
    margin: '-2.75em 0 2em 0',
  },
  list: {
    margin: 0,
    padding: 0,
    '& > li': {
      fontSize: 16,
      marginLeft: '1em',
    },
  },
}));

const registrationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('*Required'),
  password: Yup.string()
    .required('*Required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must include at least one uppercase letter')
    .matches(/[a-z]/, 'Password must include at least one lowercase letter')
    .matches(/[0-9]/, 'Password must include at least one number')
    .matches(/[`~!@#$%^&*()\-_=+{}[\]|;:'",./?]/, 'Password must include at least one special character'),
  confirmPassword: Yup.string()
    .required('*Required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

const Registration = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const errorMessage = useSelector((state: AppState) => state.auth.errorMessage);

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false; // Mark component as unmounted
      dispatch(setLoginError(null)); // Clear any login errors
    };
  }, [dispatch]);

  return (
    <div className={classes.container}>
      <div>
        <HomeNavBar />
        <img
          className={classes.bedLogo}
          src={`${BASE_URL}${IMAGE_PATHS.BED_LOGO.path}`}
          alt={`${IMAGE_PATHS.BED_LOGO.alt}`}
        />
      </div>
      <div className={classes.signInContainer}>
        <div className={classes.doctorImage} />
        <div className={classes.signInForm}>
          <Formik
            initialValues={{ email: '', password: '', confirmPassword: '' }}
            validationSchema={registrationSchema}
            onSubmit={(values, { setErrors, setSubmitting }) => {
              setSubmitting(true);

              dispatch(
                selfRegisterAPIcall(values, (error) => {
                  setSubmitting(false);

                  if (error) {
                    console.error('Registration error:', error);

                    try {
                      const parsedError = JSON.parse(error);
                      setErrors(parsedError);
                    } catch {
                      setErrors({ email: error });
                    }
                  } else {
                    console.log('Registration and login successful.');
                    history.push('/profile'); // Redirect upon success
                  }
                }),
              );
            }}
          >
            {(formProps) => (
              <form onSubmit={formProps.handleSubmit}>
                <Typography variant="h1" className={classes.signInTitle}>
                  Register
                </Typography>
                <div className={classes.fieldsContainer}>
                  <TextFieldWrapper
                    form={formProps}
                    formKey="email"
                    label="Email*"
                    variant="outlined"
                    inputLabelProps={{ className: classes.inputField }}
                    error={!!formProps.errors.email}
                    helperText={formProps.errors.email}
                  />
                  <TextFieldWrapper
                    form={formProps}
                    formKey="password"
                    label="Password*"
                    type="password"
                    variant="outlined"
                    inputLabelProps={{ className: classes.inputField }}
                  />
                  <TextFieldWrapper
                    form={formProps}
                    formKey="confirmPassword"
                    label="Confirm Password*"
                    type="password"
                    variant="outlined"
                    inputLabelProps={{ className: classes.inputField }}
                  />
                  {errorMessage && <Typography className={classes.error}>{errorMessage}</Typography>}
                  <Typography className={classes.requiredField}>*Required field</Typography>
                </div>
                <div className={classes.passwordInfo}>
                  <Typography>The password must include at least:</Typography>
                  <ul className={classes.list}>
                    <li>Eight characters</li>
                    <li>One uppercase character</li>
                    <li>One lowercase character</li>
                    <li>One number</li>
                    <li>
                      One special character,<br></br>except &lt; and &gt;
                    </li>
                  </ul>
                </div>
                <Button className={classes.signInButton} variant="contained" color="secondary" type="submit">
                  Register
                </Button>
                <div className={classes.requestAccess}>
                  <span>
                    Already have an account?
                    <Link to="/login">
                      <Button size="small" className={classes.textLinkButtons}>
                        Sign in
                      </Button>
                    </Link>
                  </span>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      <div>
        <img
          className={classes.images}
          src={`${BASE_URL}${IMAGE_PATHS.TRICOLOR_BAR.path}`}
          alt={`${IMAGE_PATHS.TRICOLOR_BAR.alt}`}
        />
        <HomeFooter />
      </div>
    </div>
  );
};

export default Registration;

// NOTE: Below is the old registration form.
// Currently, BED does not want to be able to invite users.
// However, they discussed keeping the code archived in case they do want it again.
// This code below worked with users needing to request access and click on the get started email,
// which took them to the below page. it also works with invitng the user through the admin portal.

// import React, { Fragment } from 'react';
// import { useLocation } from 'react-router-dom';
// import { useDispatch } from 'react-redux';
// import { Formik } from 'formik';
// import * as Yup from 'yup';
// import { Button, makeStyles, Theme, Typography } from '@material-ui/core';
// import TextFieldWrapper from '../../micro/TextFieldWrapper/TextFieldWrapper';
// import { registerAPIcall } from '../../../redux/actions/auth/auth.actions';
// import HomeNavBar from '../../micro/HomeNavBar/HomeNavBar';
// import HomeFooter from '../../micro/HomeFooter/HomeFooter';
// import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
// import { lightGrey } from '../../../app/theme';

// const useStyles = makeStyles((theme: Theme) => ({
//   registrationPageWrapper: {
//     minHeight: 'calc(100vh - 235px)',
//     padding: '17px 65px 65px',
//   },
//   title: {
//     margin: '17px 0 49px 0',
//     paddingBottom: '0.5em',
//     borderBottom: `1px solid ${theme.palette.secondary.main}`,
//     display: 'inline-block',
//     minWidth: 465,
//     color: theme.palette.secondary.main,
//     fontSize: 40,
//     fontWeight: 600,
//   },
//   form: {
//     width: '85%',
//     display: 'flex',
//     flexDirection: 'row',
//     justifyContent: 'space-between',
//     alignItems: 'stretch',
//   },
// formColumn: {
//   width: '30%',
//   display: 'flex',
//   flexDirection: 'column',
// },
//   inputField: {
//     color: lightGrey,
//     textTransform: 'uppercase',
//   },
//   control: {
//     width: '100%',
//     display: 'flex',
//     flexDirection: 'column',
//     marginBottom: 5,
//   },
// passwordInfo: {
//   fontSize: 16,
//   margin: 0,
// },
// list: {
//   margin: 0,
//   padding: 0,
//   '& > li': {
//     fontSize: 16,
//     marginLeft: '1em',
//   },
// },
//   submitContainer: {
//     marginTop: 40,
//     display: 'flex',
//     justifyContent: 'flex-end',
//     alignItems: 'flex-end',
//   },
//   button: {
//     borderRadius: 21,
//     height: 42,
//     minWidth: 104,
//   },
//   images: {
//     display: 'block',
//     width: '100%',
//   },
// }));

// const registerFormSchema = Yup.object({
//   firstName: Yup.string().required('*Required field'),
//   lastName: Yup.string().required('*Required field'),
//   password: Yup.string()
//     .required('*Required field')
//     .min(8, 'Password must be at least 8 characters')
//     .matches(/^(?=.*[A-Z])/, 'Password must include at least one uppercase character')
//     .matches(/^(?=.*[0-9])/, 'Password must include at least one number')
//     .matches(/^(?=.*[`~!@#$%^&*()\-_=+{}[\]\\|;:'",./?])/, 'Password must include at least one symbol')
//     .test('No angle brackets', 'Password may not contain < or >', (value) => {
//       return value && value.indexOf('<') < 0 && value.indexOf('>') < 0;
//     }),
//   confirmPassword: Yup.string()
//     .required('*Required field')
//     .oneOf([Yup.ref('password')], 'Passwords must match'),
// });

// const Registration = (): JSX.Element => {
//   const dispatch = useDispatch();
//   const classes = useStyles();
//   const params = new URLSearchParams(useLocation().search);

//   return (
//     <Fragment>
//       <HomeNavBar />
//       <div className={classes.registrationPageWrapper}>
//         <Typography className={classes.title}>Register</Typography>
//         <Formik
//           initialValues={{
//             firstName: '',
//             lastName: '',
//             password: '',
//             confirmPassword: '',
//           }}
//           validationSchema={registerFormSchema}
//           onSubmit={(values) => {
//             dispatch(registerAPIcall(params.get('token') ?? '', values));
//           }}
//           validateOnBlur={false}
//           validateOnChange={false}
//         >
//           {(formProps) => (
//             <form onSubmit={formProps.handleSubmit} className={classes.form}>
//               <div className={classes.formColumn}>
//                 <div className={classes.control}>
//                   <TextFieldWrapper
//                     form={formProps}
//                     inputLabelProps={{ className: classes.inputField }}
//                     label="FIRST NAME*"
//                     formKey="firstName"
//                     variant="outlined"
//                     autocomplete="no"
//                   />
//                 </div>
//                 <div className={classes.control}>
//                   <TextFieldWrapper
//                     form={formProps}
//                     inputLabelProps={{ className: classes.inputField }}
//                     label="LAST NAME*"
//                     formKey="lastName"
//                     variant="outlined"
//                     autocomplete="no"
//                   />
//                 </div>
//               </div>
//               <div className={classes.formColumn}>
//                 <div className={classes.control}>
//                   <TextFieldWrapper
//                     form={formProps}
//                     inputLabelProps={{ className: classes.inputField }}
//                     type="password"
//                     label="Password*"
//                     formKey="password"
//                     variant="outlined"
//                     autocomplete="new-password"
//                   />
//                 </div>
//                 <div className={classes.control}>
//                   <TextFieldWrapper
//                     form={formProps}
//                     inputLabelProps={{ className: classes.inputField }}
//                     type="password"
//                     label="Confirm Password*"
//                     formKey="confirmPassword"
//                     variant="outlined"
//                     autocomplete="no"
//                   />
//                 </div>
//               </div>
//               <div className={classes.formColumn}>
//                 <div className={classes.passwordInfo}>
//                   <Typography>The password must include at least:</Typography>
//                   <ul className={classes.list}>
//                     <li>Eight characters</li>
//                     <li>One uppercase character</li>
//                     <li>One lowercase character</li>
//                     <li>One number</li>
//                     <li>One special character, except &lt; and &gt;</li>
//                   </ul>
//                 </div>
//                 <div className={classes.submitContainer}>
//                   <Button className={classes.button} type="submit" variant="contained" color="secondary">
//                     Register
//                   </Button>
//                 </div>
//               </div>
//             </form>
//           )}
//         </Formik>
//       </div>
//       <div>
//         <img
//           className={classes.images}
//           src={`${BASE_URL}${IMAGE_PATHS.TRICOLOR_BAR.path}`}
//           alt={`${IMAGE_PATHS.TRICOLOR_BAR.alt}`}
//         />
//         <HomeFooter />
//       </div>
//     </Fragment>
//   );
// };

// export default Registration;